import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  KeyValueRow,
  KeyValueRowProvider,
} from '@/components/ui/label/label-key-value-row';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGetServerlessTaskById,
  useGetServerlessTaskLogs,
  useGetServerlessTaskSpotById,
} from '@/service/serverless-gpu/hook';
import { TypeServerless } from '@/service/serverless-gpu/type';
import { TypeServerlessLabel } from '@/service/serverless-gpu/type';
import { shortEnglishHumanizer } from '@/utils/formatTime';
import dayjs from 'dayjs';
import BadgeTaskStatus from '../components/BadgeTaskStatus';
import { LogsIcon, PencilIcon, StopCircleIcon } from 'lucide-react';
import { fCurrencyUSDExtended } from '@/utils/formatNumber';
import { Button } from '@/components/ui/button';
import ConfirmationDialog from '@/components/ui/dialog-wrapper/ConfirmationDialog';
import TaskDetailSpot from './task-spot/DetailSpot';
import { ScrollArea } from '@/components/ui/scroll-area';
type Props = {
  idTask: string;
};
const TaskDetail = ({ idTask }: Props) => {
  const { data, isLoading } = useGetServerlessTaskById({ task_id: idTask });
  const { data: dataLogs, isLoading: isLoadingLogs } = useGetServerlessTaskLogs(
    { task_id: idTask }
    // { enabled: !!data && data.trigger_type !== TypeServerless.Spot }
  );
  const { data: dataSpot, isLoading: isLoadingSpot } =
    useGetServerlessTaskSpotById(
      { task_id: idTask },
      { enabled: !!data && data.trigger_type === TypeServerless.Spot }
    );

  const dataFormatted = {
    id: idTask,
    name: data?.name,
    status: data?.status,
    created_at: dayjs(data?.created_at).format('DD MMM, HH:mm'),
    completed_at: data?.completed_at
      ? dayjs(data?.completed_at).format('DD MMM, HH:mm')
      : '-',
    duration: shortEnglishHumanizer((data?.duration ?? 0) * 1000, {
      units: ['s', 'ms'],
      delimiter: ' ',
      maxDecimalPoints: 0,
    }),
    // cost: fCurrencyUSDExtended(0),
    trigger_type: TypeServerlessLabel[data?.trigger_type as TypeServerless],
    project_id: data?.project_id,
  };

  const dataSpotFormatted = {
    max_spending: fCurrencyUSDExtended(dataSpot?.max_spending ?? 0),
  };

  return (
    <>
      <div>
        {idTask ? (
          <>
            <KeyValueRowProvider isLoading={isLoading}>
              <div className="flex flex-col gap-3 text-start">
                <KeyValueRow
                  label="Task ID"
                  value={dataFormatted.id}
                  isLoading={false}
                />
                <KeyValueRow label="Name" value={dataFormatted.name} />
                <KeyValueRow
                  label="Status"
                  value={<BadgeTaskStatus status={dataFormatted.status!} />}
                />
                <KeyValueRow label="Created" value={dataFormatted.created_at} />
                <KeyValueRow
                  label="Completed"
                  value={dataFormatted.completed_at}
                />
                <KeyValueRow label="Duration" value={dataFormatted.duration} />
                {/* <KeyValueRow label="Cost" value={dataFormatted.cost} /> */}
                <KeyValueRow
                  label="Trigger Type"
                  value={
                    <Badge variant={'secondary'}>
                      {dataFormatted.trigger_type}
                    </Badge>
                  }
                />
                <KeyValueRow
                  label="Project ID"
                  value={dataFormatted.project_id}
                />
              </div>
            </KeyValueRowProvider>
            {data?.trigger_type === TypeServerless.Spot && (
              <TaskDetailSpot idTask={idTask} status={data?.status} />
            )}

            <div className="mt-8 flex flex-col">
              <div className="flex items-center gap-2">
                <LogsIcon className="h-4 w-4" />
                <p className="text-lg font-medium text-foreground">Logs</p>
              </div>
              <Separator className="mb-4 mt-2" />
              <Card className="p-2 shadow-none">
                <ScrollArea className="h-full min-h-[200px] [&>[data-radix-scroll-area-viewport]]:max-h-[600px]">
                  {isLoadingLogs ? (
                    <div className="flex flex-col gap-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  ) : (
                    <pre className="whitespace-pre-wrap">{dataLogs?.log}</pre>
                  )}
                </ScrollArea>
              </Card>
            </div>
          </>
        ) : (
          <p>No task selected</p>
        )}
      </div>
    </>
  );
};

export default TaskDetail;
